import { Machine } from '@internal/utils/machine/Machine'
import { MachineType } from '@internal/utils/machine/MachineType'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import {
  MachineComparisonContext,
  TComparisonAlertType,
} from './MachineComparisonContext'

const maxMachinesToCompare = 3

type Props = {
  machineType: MachineType | undefined
  children: ReactNode
}
export const MachineComparisonContextProvider: React.FC<Props> = React.memo(
  ({ machineType, children }) => {
    const [machines, setMachines] = useState<Machine[]>([])
    const [isCompareModeEnabled, setCompareModeEnabled] = useState(false)
    const [comparisonAlertType, setComparisonAlertType] =
      useState<TComparisonAlertType>(null)
    const [currentMachineType, setCurrentMachineType] = useState<
      MachineType | undefined
    >(machineType)

    const leaveCompareMode = useCallback(() => {
      setCompareModeEnabled(false)
      setMachines([])
      setComparisonAlertType(null)
    }, [setCompareModeEnabled, setComparisonAlertType, setMachines])

    const _setCurrentMachineType = useCallback(
      (machineType: MachineType | undefined) => {
        if (isCompareModeEnabled && currentMachineType !== machineType) {
          leaveCompareMode()
          setComparisonAlertType('changedMachineType')
        }
      },
      [
        currentMachineType,
        isCompareModeEnabled,
        leaveCompareMode,
        setComparisonAlertType,
      ]
    )

    useEffect(() => {
      if (isCompareModeEnabled) {
        _setCurrentMachineType(machineType)
      }
    }, [isCompareModeEnabled, machineType, _setCurrentMachineType])

    const isSelectedForComparison = useCallback(
      (machine: Machine) =>
        !!machines.find((m) => m.machineGroupId === machine.machineGroupId),
      [machines]
    )

    const enterCompareMode = useCallback(
      (machineType: MachineType) => {
        setCurrentMachineType(machineType)
        setCompareModeEnabled(true)
      },
      [setCompareModeEnabled, setCurrentMachineType]
    )

    const addMachine = useCallback(
      (machine: Machine) => {
        if (machines.length < maxMachinesToCompare) {
          setMachines((machines) => [...machines, machine])
        } else {
          setComparisonAlertType('reachedMaxMachines')
        }
      },
      [machines.length, setComparisonAlertType, setMachines]
    )

    const toggleCompareMode = useCallback(
      (machineType: MachineType) => {
        if (isCompareModeEnabled) {
          leaveCompareMode()
        } else {
          enterCompareMode(machineType)
        }
      },
      [enterCompareMode, isCompareModeEnabled, leaveCompareMode]
    )

    const removeMachine = useCallback(
      (machineGroupId: string) => {
        setMachines((machines) =>
          machines.filter((m) => m.machineGroupId !== machineGroupId)
        )
      },
      [setMachines]
    )

    const dismissAlert = useCallback(
      () => setComparisonAlertType(null),
      [setComparisonAlertType]
    )

    return (
      <MachineComparisonContext.Provider
        value={{
          addMachine,
          removeMachine,
          enterCompareMode,
          leaveCompareMode,
          isCompareModeEnabled,
          machines,
          setCurrentMachineType: _setCurrentMachineType,
          isSelectedForComparison,
          toggleCompareMode,
          dismissAlert,
          comparisonAlertType,
          maxMachinesToCompare,
          readyForComparison: machines.length >= 2,
        }}
      >
        {children}
      </MachineComparisonContext.Provider>
    )
  }
)

MachineComparisonContextProvider.displayName =
  'MachineComparisonContextProvider'
